import { ComponentPropsWithoutRef } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import * as Avatar from '@givebutter/primitives/avatar';
import { Box, BoxProps } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { faXTwitter } from '@fortawesome/free-brands-svg-icons';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { faEnvelope, faPhone } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ContactDateOfBirth } from 'assets/js/pages/contacts/ContactDateOfBirth';
import { getContactFormQueryParams } from 'assets/js/pages/contacts/contactFormParams';
import { CopyButton } from 'components/copy-button';
import {
    AddressReadable,
    copyAddress,
} from 'components/pages/campaign-settings/campaign-auction/AddressReadable';
import { TruncatedText } from 'components/truncated-text';
import { EmptyData } from 'components/util';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import { getFormattedPhoneNumber } from 'helpers/formatting';
import { twMerge } from 'tailwind-merge';

const ContactSidebarPrimaryInfo = ({ children, ...props }: BoxProps) => {
    const match = useRouteMatch();
    const history = useHistory();

    return (
        <Box className={'flex flex-col'} {...props}>
            <div className={'flex items-center justify-between px-4 py-3'}>
                <p className={'font-medium text-gray-900'}>Primary Details</p>
                <Button
                    variant="none"
                    color="primary"
                    size="sm"
                    className="font-medium"
                    onClick={() => history.push(`${match.url}/profile/details/edit`)}
                >
                    <FontAwesomeIcon icon={faEdit} size="sm" /> Edit
                </Button>
            </div>
            <Separator />
            <div className={'flex flex-col gap-3 p-4'}>{children}</div>
        </Box>
    );
};

const ContactSidebarItemTitle = ({ children, ...props }: ComponentPropsWithoutRef<'p'>) => {
    return (
        <p className={'text-sm text-gray-600'} {...props}>
            {children}
        </p>
    );
};

const ContactSidebarItem = ({ children, className, ...props }: ComponentPropsWithoutRef<'div'>) => {
    return (
        <div className={twMerge('flex items-center justify-between gap-2', className)} {...props}>
            {children}
        </div>
    );
};

const ContactSidebarEmailContent = () => {
    const history = useHistory();
    const location = useLocation();
    const { data: contact } = useCurrentContact();

    return contact.primary_email ? (
        <div className={'flex max-w-[230px] items-center'}>
            <TruncatedText>
                <p>{contact.primary_email}</p>
            </TruncatedText>
            <CopyButton text={contact.primary_email} />
        </div>
    ) : (
        <Button
            variant="none"
            color="primary"
            className="font-medium"
            onClick={() =>
                history.push({
                    pathname: `${location.pathname}/details/edit`,
                    search: getContactFormQueryParams({
                        only: ['emails'],
                        autoFocus: 'emails.0.value',
                    }),
                })
            }
        >
            + Add Email
        </Button>
    );
};

const ContactSidebarSendEmail = () => {
    const { data: contact } = useCurrentContact();
    const { emailConfig } = useCurrentAccount();

    return (
        !!contact.primary_email && (
            <a href={`mailto:${contact.primary_email}?bcc=${emailConfig.bccAddress}`}>
                <Avatar.Root className={'!h-9 !w-9 !bg-blue-50'}>
                    <Avatar.Fallback aria-label="Send email">
                        <FontAwesomeIcon className={'text-blue-500'} icon={faEnvelope} />
                    </Avatar.Fallback>
                </Avatar.Root>
            </a>
        )
    );
};

const ContactSidebarIndividualEmail = (props: ComponentPropsWithoutRef<'div'>) => {
    return (
        <ContactSidebarItem {...props}>
            <div className={'flex flex-col overflow-hidden'}>
                <ContactSidebarItemTitle>Email</ContactSidebarItemTitle>
                <ContactSidebarEmailContent />
            </div>
            <ContactSidebarSendEmail />
        </ContactSidebarItem>
    );
};

const ContactSidebarCompanyEmail = (props: ComponentPropsWithoutRef<'div'>) => {
    return (
        <ContactSidebarItem {...props}>
            <div className={'flex flex-col overflow-hidden'}>
                <ContactSidebarItemTitle>Company email</ContactSidebarItemTitle>
                <ContactSidebarEmailContent />
            </div>
            <ContactSidebarSendEmail />
        </ContactSidebarItem>
    );
};

const ContactSidebarPhoneContent = () => {
    const history = useHistory();
    const location = useLocation();
    const { data: contact } = useCurrentContact();

    return contact.primary_phone ? (
        <div className={'flex items-center'}>
            <p>{getFormattedPhoneNumber(contact.primary_phone)}</p>
            <CopyButton text={contact.primary_phone} />
        </div>
    ) : (
        <Button
            variant="none"
            color="primary"
            className="font-medium"
            onClick={() =>
                history.push({
                    pathname: `${location.pathname}/details/edit`,
                    search: getContactFormQueryParams({
                        only: ['phones'],
                        autoFocus: 'phones.0.value',
                    }),
                })
            }
        >
            + Add Phone
        </Button>
    );
};

const ContactSidebarItemCall = () => {
    const { data: contact } = useCurrentContact();

    return (
        !!contact.primary_phone && (
            <a href={`tel:${contact.primary_phone}`}>
                <Avatar.Root className={'!h-9 !w-9 !bg-blue-50'}>
                    <Avatar.Fallback aria-label={`Call ${contact.name_display}`}>
                        <FontAwesomeIcon className={'text-blue-500'} icon={faPhone} />
                    </Avatar.Fallback>
                </Avatar.Root>
            </a>
        )
    );
};

const ContactSidebarIndividualPhone = (props: ComponentPropsWithoutRef<'div'>) => {
    return (
        <ContactSidebarItem {...props}>
            <div className={'flex flex-col'}>
                <ContactSidebarItemTitle>Phone</ContactSidebarItemTitle>
                <ContactSidebarPhoneContent />
            </div>
            <ContactSidebarItemCall />
        </ContactSidebarItem>
    );
};

const ContactSidebarCompanyPhone = (props: ComponentPropsWithoutRef<'div'>) => {
    return (
        <ContactSidebarItem {...props}>
            <div className={'flex flex-col'}>
                <ContactSidebarItemTitle>Company phone</ContactSidebarItemTitle>
                <ContactSidebarPhoneContent />
            </div>
            <ContactSidebarItemCall />
        </ContactSidebarItem>
    );
};

const ContactSidebarAddress = (props: ComponentPropsWithoutRef<'div'>) => {
    const history = useHistory();
    const location = useLocation();
    const { data: contact } = useCurrentContact();

    return (
        <div className={'flex flex-col'}>
            <ContactSidebarItemTitle>Address</ContactSidebarItemTitle>
            {contact.primary_address ? (
                <div className={'flex items-end'}>
                    <div className={'[&_*]:text-base'}>
                        <AddressReadable address={contact.primary_address} />
                    </div>
                    <CopyButton text={copyAddress(contact.primary_address)} />
                </div>
            ) : (
                <Button
                    variant="none"
                    color="primary"
                    className="font-medium"
                    onClick={() => history.push(`${location.pathname}/addresses/new`)}
                >
                    + Add Address
                </Button>
            )}
        </div>
    );
};

const ContactSidebarBirthday = () => {
    const history = useHistory();
    const location = useLocation();
    const { data: contact } = useCurrentContact();

    return (
        <div className={'flex flex-col'}>
            <ContactSidebarItemTitle>Birthday</ContactSidebarItemTitle>
            <p>
                {contact.dob ? (
                    <ContactDateOfBirth dob={contact.dob} />
                ) : (
                    <Button
                        variant="none"
                        color="primary"
                        className="font-medium"
                        onClick={() =>
                            history.push({
                                pathname: `${location.pathname}/details/edit`,
                                search: getContactFormQueryParams({
                                    autoFocus: 'dob',
                                }),
                            })
                        }
                    >
                        + Add Birthday
                    </Button>
                )}
            </p>
        </div>
    );
};

const ContactSidebarEmployer = () => {
    const match = useRouteMatch();
    const history = useHistory();
    const { data: contact } = useCurrentContact();

    return (
        <div className={'flex flex-col'}>
            <ContactSidebarItemTitle>Employer</ContactSidebarItemTitle>
            <p>
                {contact.employer ? (
                    contact.employer
                ) : (
                    <Button
                        variant="none"
                        color="primary"
                        className="font-medium"
                        onClick={() => history.push(`${match.url}/profile/details/edit`)}
                    >
                        + Add Employer
                    </Button>
                )}
            </p>
        </div>
    );
};

const ContactSidebarSocialItem = ({ url }: { url?: string | null }) => {
    return (
        <div className="overflow-hidden">
            {url ? (
                <a rel="noreferrer" target="_blank" href={url}>
                    <TruncatedText>{url}</TruncatedText>
                </a>
            ) : (
                <EmptyData />
            )}
        </div>
    );
};

const ContactSidebarSocials = () => {
    const { data: contact } = useCurrentContact();
    const { website_url, linkedin_url, twitter_url, facebook_url } = contact;
    return (
        <>
            <div className="flex flex-col">
                <ContactSidebarItemTitle>Website</ContactSidebarItemTitle>
                <ContactSidebarSocialItem url={website_url} />
            </div>
            <div className="flex flex-col">
                <ContactSidebarItemTitle>LinkedIn</ContactSidebarItemTitle>
                <ContactSidebarSocialItem url={linkedin_url} />
            </div>
            <div className="flex flex-col">
                <ContactSidebarItemTitle>
                    <FontAwesomeIcon icon={faXTwitter} />
                    <span className="sr-only">X</span>
                </ContactSidebarItemTitle>
                <ContactSidebarSocialItem url={twitter_url} />
            </div>
            <div className="flex flex-col">
                <ContactSidebarItemTitle>Facebook</ContactSidebarItemTitle>
                <ContactSidebarSocialItem url={facebook_url} />
            </div>
        </>
    );
};

const Root = ContactSidebarPrimaryInfo;
const CompanyEmail = ContactSidebarCompanyEmail;
const IndividualEmail = ContactSidebarIndividualEmail;
const CompanyPhone = ContactSidebarCompanyPhone;
const IndividualPhone = ContactSidebarIndividualPhone;
const Address = ContactSidebarAddress;
const Birthday = ContactSidebarBirthday;
const Employer = ContactSidebarEmployer;
const Socials = ContactSidebarSocials;

export {
    ContactSidebarPrimaryInfo,
    ContactSidebarIndividualEmail,
    ContactSidebarCompanyEmail,
    ContactSidebarIndividualPhone,
    ContactSidebarCompanyPhone,
    ContactSidebarAddress,
    ContactSidebarBirthday,
    ContactSidebarEmployer,
    ContactSidebarSocials,
    //
    Root,
    CompanyEmail,
    CompanyPhone,
    IndividualEmail,
    IndividualPhone,
    Address,
    Birthday,
    Employer,
    Socials,
};
